import api from '../utils/api';

export interface ProContact {
  _id: string;
  PID: number;
  INPUT: Date;
  RELATION?: string;
  PROFESSION?: string;
  /** Canonical code from the Profession collection (ARCH, PE, CPA, LAW...) */
  PROFESSIONID?: string;
  MR?: string;
  LAST?: string;
  FIRST?: string;
  COMPANY?: string;
  TITLE?: string;
  TEL_BUS?: string;
  TEL_BUS2?: string;
  TEL_FAX?: string;
  TEL_RES?: string;
  TEL_CEL?: string;
  TEL_PAG?: string;
  EMAIL?: string;
  STREET?: string;
  CITY?: string;
  STATE?: string;
  ZIP?: string;
  NOTE?: string;
  SOURCE?: string;
  MRETURN?: boolean;
  MRETURNDATE?: Date;
  RATE?: string;
  PROSTATUS?: string;
  EMAILOPTOUT?: boolean;
  EMAILOPTOUTDATE?: Date;
  EMAILOPTOUTREASON?: string;
  CONVERTEDTOUSER?: boolean;
  CONVERTEDDATE?: Date;
  CONVERTEDUSERID?: string;
  UNSUBSCRIBETOKEN?: string;
  sourceid?: string;
  WEB?: string;
  SOURCEDETAIL?: string;
  /** listIDs (ContactList.listID) this contact belongs to */
  LISTS?: number[];
}

export interface ContactList {
  _id: string;
  listID: number;
  name: string;
  description?: string;
  contactCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface MktEmail {
  _id: string;
  mktemailid: number;
  mktemaildate: Date;
  mktemailname: string;
  mktemailtype?: string;
  mktemailpage?: string;
  mktemailnote?: string;
  connmktemailimgid?: number;
  html?: string;
}

export interface Campaign {
  _id: string;
  campaignid: number;
  campaigninput: Date;
  campaignsent?: Date;
  campaignname: string;
  campaignsentemails: number[];
  emailtemplateid?: number;
  emailsubject?: string;
  emailbody?: string;
  campaignnote?: string;
  campaignsentto: number[];
  status: 'draft' | 'scheduled' | 'sent' | 'cancelled';
  scheduledDate?: Date;
}

// ProContact API
export const getAllContacts = async (filters?: {
  search?: string;
  state?: string;
  type?: string;
  relation?: string;
  emailOptout?: boolean;
  lists?: number[];
}): Promise<ProContact[]> => {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.state) params.append('state', filters.state);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.relation) params.append('relation', filters.relation);
  if (filters?.emailOptout !== undefined) params.append('emailOptout', filters.emailOptout.toString());
  if (filters?.lists && filters.lists.length > 0) params.append('lists', filters.lists.join(','));

  const response = await api.get(`/api/marketing/contacts?${params.toString()}`);
  return response.data;
};

export const createContact = async (data: Partial<ProContact>): Promise<ProContact> => {
  const response = await api.post('/api/marketing/contacts', data);
  return response.data;
};

export const updateContact = async (id: string, data: Partial<ProContact>): Promise<ProContact> => {
  const response = await api.put(`/api/marketing/contacts/${id}`, data);
  return response.data;
};

export const deleteContact = async (id: string): Promise<void> => {
  await api.delete(`/api/marketing/contacts/${id}`);
};

export const bulkDeleteContacts = async (ids: string[]): Promise<{ deletedCount: number }> => {
  const response = await api.delete('/api/marketing/contacts/bulk', { data: { ids } });
  return response.data;
};

// ContactList API
export const getAllLists = async (): Promise<ContactList[]> => {
  const response = await api.get('/api/marketing/lists');
  return response.data;
};

export const createList = async (data: { name: string; description?: string }): Promise<ContactList> => {
  const response = await api.post('/api/marketing/lists', data);
  return response.data;
};

export const updateList = async (id: string, data: { name?: string; description?: string }): Promise<ContactList> => {
  const response = await api.put(`/api/marketing/lists/${id}`, data);
  return response.data;
};

export const deleteList = async (id: string): Promise<void> => {
  await api.delete(`/api/marketing/lists/${id}`);
};

export const addContactsToList = async (id: string, pids: number[]): Promise<{ message: string; matchedCount: number }> => {
  const response = await api.post(`/api/marketing/lists/${id}/contacts`, { pids });
  return response.data;
};

export const removeContactsFromList = async (id: string, pids: number[]): Promise<{ message: string; matchedCount: number }> => {
  const response = await api.delete(`/api/marketing/lists/${id}/contacts`, { data: { pids } });
  return response.data;
};

// MktEmail API
export const getAllMktEmails = async (): Promise<MktEmail[]> => {
  const response = await api.get('/api/marketing/emails');
  return response.data;
};

export const createMktEmail = async (data: Partial<MktEmail>): Promise<MktEmail> => {
  const response = await api.post('/api/marketing/emails', data);
  return response.data;
};

export const updateMktEmail = async (id: string, data: Partial<MktEmail>): Promise<MktEmail> => {
  const response = await api.put(`/api/marketing/emails/${id}`, data);
  return response.data;
};

export const deleteMktEmail = async (id: string): Promise<void> => {
  await api.delete(`/api/marketing/emails/${id}`);
};

// Campaign API
export const getAllCampaigns = async (): Promise<Campaign[]> => {
  const response = await api.get('/api/marketing/campaigns');
  return response.data;
};

export const getCampaignById = async (id: string): Promise<Campaign> => {
  const response = await api.get(`/api/marketing/campaigns/${id}`);
  return response.data;
};

export const createCampaign = async (data: Partial<Campaign>): Promise<Campaign> => {
  const response = await api.post('/api/marketing/campaigns', data);
  return response.data;
};

export const updateCampaign = async (id: string, data: Partial<Campaign>): Promise<Campaign> => {
  const response = await api.put(`/api/marketing/campaigns/${id}`, data);
  return response.data;
};

export const deleteCampaign = async (id: string): Promise<void> => {
  await api.delete(`/api/marketing/campaigns/${id}`);
};

export const sendCampaign = async (id: string, scheduledDate?: Date): Promise<{
  message: string;
  sentCount?: number;
  totalRecipients?: number;
  errors?: any[];
  campaign?: Campaign;
}> => {
  const response = await api.post(`/api/marketing/campaigns/${id}/send`, {
    scheduledDate: scheduledDate?.toISOString()
  });
  return response.data;
};

export const sendTestEmail = async (id: string, testEmail: string) => {
  const response = await api.post(`/api/marketing/campaigns/${id}/test`, { testEmail });
  return response.data;
};

// Email Logs
export interface EmailLog {
  _id: string;
  emaillogid: number;
  PID: number;
  campaignid?: number;
  emailto: string;
  emailsubject: string;
  emailbody?: string;
  senttimestamp: Date;
  status: 'sent' | 'failed' | 'bounced' | 'opened' | 'clicked';
  errorMessage?: string;
  openedtimestamp?: Date;
  clickedtimestamp?: Date;
}

export interface ContactEmailHistory {
  contact: {
    PID: number;
    FIRST?: string;
    LAST?: string;
    EMAIL?: string;
  };
  logs: EmailLog[];
  stats: {
    sent?: number;
    failed?: number;
    opened?: number;
    clicked?: number;
  };
  totalSent: number;
}

export const getContactEmailHistory = async (PID: number): Promise<ContactEmailHistory> => {
  const response = await api.get(`/api/emaillogs/contact/${PID}`);
  return response.data;
};

// AI Email Generation
export interface AIEmailRequest {
  topic: string;
  purpose: 'welcome' | 'newsletter' | 'reminder' | 'promotional' | 'reengagement' | 'announcement' | 'campaign';
  targetAudience: string;
  tone: 'professional' | 'friendly' | 'urgent';
  includeImage: boolean;
  imageStyle: 'auto' | 'photorealistic' | 'infographic' | 'simple';
  additionalContext?: string;
  uploadedImageUrl?: string;
  colorScheme?: 'navy-teal' | 'navy-amber' | 'forest-gold' | 'slate-sky' | 'burgundy-champagne';
  topLogoLink?: string;
  heroImageLink?: string;
  includeSampleCards?: boolean;
}

export interface AIEmailResult {
  html: string;
  suggestedName: string;
  imageUrl: string | null;
}

export const generateEmailWithAI = async (data: AIEmailRequest): Promise<AIEmailResult> => {
  const response = await api.post('/api/marketing/emails/generate-ai', data);
  return response.data;
};

export const getUnsubscriberCounts = async (): Promise<Record<string, number>> => {
  const response = await api.get('/api/marketing/unsubscriber-counts');
  return response.data;
};

export const getCampaignUnsubscribers = async (id: string): Promise<{ count: number; contacts: any[] }> => {
  const response = await api.get(`/api/marketing/campaigns/${id}/unsubscribers`);
  return response.data;
};

export interface CampaignFailedContact {
  PID: number;
  emailto: string;
  errorMessage?: string;
  senttimestamp: string;
}

export interface CampaignResults {
  campaign: {
    campaignid: number;
    campaignname: string;
    status: string;
    campaignsent?: string;
    totalRecipients: number;
  };
  totals: Record<string, number>;
  current: Record<string, number>;
  failedContacts: CampaignFailedContact[];
}

export const getCampaignResults = async (id: string): Promise<CampaignResults> => {
  const response = await api.get(`/api/marketing/campaigns/${id}/results`);
  return response.data;
};

export interface RetryFailedResult {
  message: string;
  retriedCount: number;
  sentCount: number;
  failedCount: number;
}

export const retryCampaignFailed = async (id: string): Promise<RetryFailedResult> => {
  const response = await api.post(`/api/marketing/campaigns/${id}/retry-failed`);
  return response.data;
};

export const uploadHeroImage = async (file: File): Promise<{ imageUrl: string }> => {
  const form = new FormData();
  form.append('image', file);
  const response = await api.post('/api/marketing/emails/upload-hero-image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const sendTestEmailToAll = async (html: string, subject: string, emails: string[]): Promise<{ message: string; results: { email: string; sent: boolean; error?: string }[] }> => {
  const response = await api.post('/api/marketing/emails/test-send', { html, subject, emails });
  return response.data;
};

// Top Logo Config
export const getTopLogoImage = async (): Promise<{ imageUrl: string | null }> => {
  const response = await api.get('/api/marketing/config/top-logo');
  return response.data;
};

export const uploadTopLogoImage = async (file: File): Promise<{ imageUrl: string }> => {
  const form = new FormData();
  form.append('image', file);
  const response = await api.post('/api/marketing/config/upload-top-logo', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteTopLogoImage = async (): Promise<void> => {
  await api.delete('/api/marketing/config/top-logo');
};
